import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Chip,
  Avatar,
  IconButton,
  Button,
  Stack,
  Divider,
} from '@mui/material';
import RequestInfoCard from './RequestInfoCard';
import useAPICall from '../../hooks/useAPICall';
import { API_CONFIG } from '../../config/api.config';
import GlobalMaterialTable from '../../components/common/GlobalMaterialTable';
import moment from 'moment';

const StatusChip = ({ status }) => {
  const statusConfig = {
    New: {
      color: '#2563EB',
      bgcolor: '#EAF2FF',
    },
    'In Review': {
      color: '#EA7A1A',
      bgcolor: '#FFF2E7',
    },
    'Pending Info': {
      color: '#5B4CE6',
      bgcolor: '#F0ECFF',
    },
    Approved: {
      color: '#16A34A',
      bgcolor: '#EAFBF0',
    },
    Rejected: {
      color: '#DC2626',
      bgcolor: '#FDECEC',
    },
  };

  const { color, bgcolor } = statusConfig[status] || {
    color: '#64748b',
    bgcolor: '#f1f5f9',
  };

  return (
    <Chip
      label={status}
      size="small"
      sx={{
        color,
        bgcolor,
        fontWeight: 700,
        
        height: 20,
        borderRadius: '6px',
        px: 0.5,
        '& .MuiChip-label': {
          px: 1,
        },
      }}
    />
  );
};



const DrugInbox = () => {
  const navigate = useNavigate();
  const [selectedRow, setSelectedRow] = useState(null);
  const { loading, error, data, execute } = useAPICall();
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });

  useEffect(() => {
    const load = async () => {
      try {
        await execute(API_CONFIG.inbox.getDrugRequests, null, {
          errorMessage: 'Unable to load drug batches.',
          retries: 1,
        });
      } catch {
      }
    };
    load();
  }, [execute]);

  const rows = useMemo(() => (Array.isArray(data) ? data : data?.data || data?.results || []), [data]);
  useEffect(() => {
    if (rows.length > 0 && !selectedRow) {
      setSelectedRow(rows[0]);
    }
  }, [rows, selectedRow]);
  const getSource = (row) => row?.batchRequests?.[0]?.request?.source || '-';
  const getNdcCount = (row) => (Array.isArray(row.batchRequests) ? row.batchRequests.length : 0);

  const formatTimestamp = (value) =>
  value ? moment(value).format('YYYY-MM-DD HH:mm:ss') : '-';
  const onOpenBatch = (row) => {
    if (!row?.batchId) return;
    const status = row.isBatchMatched ? 'matched' : 'un-matched';
    navigate(`/approve-ndc?batch-id=${row.batchId}&status=${status}`);
  };
  const columns = useMemo(() => [
    { header: 'Batch ID', id: 'batchId', accessorFn: (row) => row.batchId, size: 120 },
    { header: 'Source', id: 'source', accessorFn: (row) => getSource(row), size: 120 },
    { header: 'Status', id: 'status', accessorFn: (row) => row.batchStatus, size: 120, Cell: ({ cell }) => <StatusChip status={cell.getValue()} /> },
    { header: 'NDC Count', id: 'ndcCount', accessorFn: (row) => getNdcCount(row), size: 130 },
    { header: 'Created Date', id: 'createdDate', accessorFn: (row) => formatTimestamp(row.batchCreatedTime), size: 160 },
    { header: 'Last Updated', id: 'lastUpdated', accessorFn: (row) => formatTimestamp(row.batchLastUpdatedTime), size: 160 },
  ], []);

  const TitleComponent = (
    <Stack direction="row" alignItems="center" spacing={2}>
      <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#0f172a' }}>Active Requests</Typography>
      <Chip label={rows.length} size="small" sx={{ bgcolor: '#eff6ff', color: '#3b82f6', fontWeight: 700, height: 24, borderRadius: 1 }} />
    </Stack>
  );

  return (
    <Box sx={{ p: 0 }}>
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Paper sx={{ boxShadow: '0 1px 3px rgba(0,0,0,0.1)', borderRadius: 2, overflow: 'hidden' }}>
            <GlobalMaterialTable
              state={{ isLoading: loading }}
              columns={columns}
              data={rows}
              emptyMessage="No Open Requests"
              enablePagination
              enableBottomToolbar
              manualPagination={false}
              page={pagination.pageIndex + 1}
              pageSize={pagination.pageSize}
              onPageChange={(p) => setPagination(prev => ({ ...prev, pageIndex: p - 1 }))}
              onPageSizeChange={(ps) => setPagination(prev => ({ ...prev, pageSize: ps }))}
              tableTitle="Drug Inbox"
              tableCount={rows.length}
              muiTablePaperProps={{
                elevation: 0,
                sx: {
                  border: 'none !important',
                  boxShadow: 'none !important',
                },
              }}
              muiTableBodyRowProps={({ row }) => ({
                onDoubleClick: () => onOpenBatch(row.original),
                 onClick: () => {
                    setSelectedRow({id: row.original.batchId});
                  },
                sx: {
                  cursor: 'pointer',
                  backgroundColor: selectedRow?.id === row.original.batchId ? '#eaf4ff' : 'inherit',
                  '&:hover': { backgroundColor: '#f1f5f9' }
                },
              })}
            />
          </Paper>
        </Box>
      </Box>
    </Box>
  );
};

export default DrugInbox;
